import React, { useEffect, useRef, useState } from "react";
import { Dropdown, DropdownProps, Form, TextArea } from "semantic-ui-react";

import ModConfirm from "react-lib/apps/common/cnmi/ModConfirm";
import ModInfo from "react-lib/apps/common/ModInfo";
import ErrorMessage from "react-lib/apps/common/ErrorMessage";

export type ChatMessageTemplateOption = {
  key: string | number;
  value: string | number;
  text: string;
  message: string;
};

export type SendChatMessageData = {
  template: ChatMessageTemplateOption["value"] | null;
  message: string;
};

type ModSendChatMessageProps = {
  open: boolean;
  options?: ChatMessageTemplateOption[];
  titleName?: string;
  // return ผลของ API ([response, error]) เพื่อแสดงผลการส่ง
  onApprove: (data: SendChatMessageData) => any;
  onClose: () => void;
};

const ModSendChatMessage = (props: ModSendChatMessageProps) => {
  const [messageTemplate, setMessageTemplate] = useState<SendChatMessageData["template"]>(null);
  const [chatMessage, setChatMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [sendResult, setSendResult] = useState<{ error?: any } | null>(null);

  const textareaRef = useRef<any>();
  const isTextareaChange = useRef<boolean>(false);

  // Reset to the first template every time the modal is opened
  useEffect(() => {
    if (props.open) {
      isTextareaChange.current = false;

      handleSelectMessage(null);
    }
  }, [props.open]);

  useEffect(() => {
    if (isTextareaChange.current) {
      textareaRef.current?.ref?.current?.focus();
    }
  }, [chatMessage]);

  const handleSelectMessage = (e: any, v: DropdownProps | null = null) => {
    const value = (v?.value as SendChatMessageData["template"]) || props.options?.[0]?.value || null;

    setMessageTemplate(value);
    setChatMessage(props.options?.find((item) => item.value === value)?.message || "");
  };

  const handleApprove = async () => {
    setIsSending(true);

    const result = await props.onApprove({ template: messageTemplate, message: chatMessage });

    setIsSending(false);

    props.onClose();

    if (Array.isArray(result)) {
      setSendResult({ error: result[1] });
    }
  };

  const handleClose = () => {
    if (!isSending) {
      props.onClose();
    }
  };

  const handleCloseResult = () => {
    setSendResult(null);
  };

  return (
    <>
      <ModConfirm
        openModal={props.open}
        titleColor="blue"
        titleName={props.titleName || "ส่งข้อความ"}
        basic={false}
        approveButtonText="OK"
        approveButtonColor="teal"
        approveButtonLoading={isSending}
        disabledApproveButton={!chatMessage.trim() || isSending}
        denyButtonText="Cancel"
        denyButtonColor="grey"
        disabledDenyButton={isSending}
        onDeny={handleClose}
        onApprove={handleApprove}
        onCloseWithDimmerClick={handleClose}
        content={
          <Form>
            <Dropdown
              value={messageTemplate ?? undefined}
              selection={true}
              options={props.options || []}
              fluid
              style={{ width: "100%", marginBottom: "1.25rem" }}
              onChange={handleSelectMessage}
            />
            <TextArea
              ref={textareaRef}
              value={chatMessage}
              rows={5}
              onChange={(e: any, v) => {
                isTextareaChange.current = true;
                setChatMessage(v.value as string);
              }}
            />
          </Form>
        }
      />

      <ModInfo
        open={!!sendResult}
        type={sendResult?.error ? "error" : "success"}
        titleName={sendResult?.error ? "ส่งข้อความไม่สำเร็จ" : "ส่งข้อความสำเร็จ"}
        onClose={handleCloseResult}
        onApprove={handleCloseResult}
      >
        {sendResult?.error && <ErrorMessage error={sendResult.error} style={{ margin: "1rem 0 0.75rem" }} />}
      </ModInfo>
    </>
  );
};

ModSendChatMessage.displayName = "ModSendChatMessage";

export default ModSendChatMessage;
